// Copyright 2023, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'

export default {
	data() { return {
		folder_handling_option: null,	// will be set to 'assets_only' or 'import_folders'
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),
		collection_editor_case_import_url: {
			get() { return this.$store.state.lst.collection_editor_case_import_url },
			set(val) { this.$store.commit('lst_set', ['collection_editor_case_import_url', val]) }
		},
		collection_editor_case_branch_identifier: {
			get() { return this.$store.state.lst.collection_editor_case_branch_identifier },
			set(val) { this.$store.commit('lst_set', ['collection_editor_case_branch_identifier', val]) }
		},
	},
	watch: {
	},
	methods: {
		async import_from_case_start(dialog_title) {
			// ask for the api url or Satchel url
			let api_url = await U.prompt_promise({
				title: dialog_title,
				// text: 'Enter the identifier (GUID) for the CASE resource framework.',
				text: 'Enter one of the following:<br>'
					+ '    a. The CASE REST API url for the CASE resource framework you’d like to import from, e.g.:<br>'
					+ '           <span style="font-size:14px">https://case.georgiastandards.org/ims/case/v1p0/CFPackages/f4b2bbd4-d7e6-4666-b747-74094a1c6e4a</span><br>'
					+ '    b. The Satchel url for the “branch” of the CASE resource framework you’d like to import from, e.g.:<br>'
					+ '           <span style="font-size:14px">https://case.georgiastandards.org/26f3c45c-c053-48a0-af55-c60b2a89033e/c08eef85-7347-4062-be7c-0cedd92121ca/2</span><br>'
					,

				initialValue: this.collection_editor_case_import_url,

				disableForEmptyValue: true,
				acceptText: 'Next',
				acceptIconAfter: 'fas fa-circle-arrow-right',
				extraBtnText: 'Upload Framework',
				dialogMaxWidth: 900,
			})
			if (api_url == false) {
				return
			}

			let framework_identifier, branch_identifier, fr
			
			/////////////////////////////////
			// if user clicked the extra btn, show dialog to upload a framework
			if (api_url == 'extra') {
				let file = await U.prompt_promise({
					title: 'Upload Resource Framework for Importing',
					text: 'Choose a CASE JSON file from your computer as the imported resource framework source.',
					promptType: 'file', // default is 'text'
					acceptText: 'Process File',
					acceptIconAfter: 'fas fa-circle-arrow-right',
					disableForEmptyValue: true,
				})
				if (empty(file) || empty(file.name)) return

				let file_text = await new Promise((resolve, reject) => {
					let file_reader = new FileReader()
					file_reader.onload = event => resolve(event.target.result)
					file_reader.onerror = () => reject(new Error('File read failed'))
					file_reader.readAsText(file)
				})

				let json
				try {
					json = JSON.parse(file_text)
				} catch (error) {
					console.log(error)
					this.$alert('The uploaded file was not valid JSON (an error occurred when attempting to parse the file).')
					return
				}

				if (!json.CFDocument) {
					this.$alert('The uploaded JSON did not include a CFDocument property, so it is not a valid CASE framework file.')
					return
				}

				console.warn('got json:', json)

				framework_identifier = json.CFDocument.identifier

				// then get branch identifier
				branch_identifier = await U.prompt_promise({
					title: dialog_title,
					text: 'Click IMPORT ENTIRE FRAMEWORK to import all assets, as well as “folders” and subfolders, from the framework. Or enter the identifier (GUID) for the CFItem (which could represent a “branch” or an individual asset) of the CASE framework and click IMPORT BRANCH OR ASSET.',
					initialValue: this.collection_editor_case_branch_identifier,
					extraBtnText: 'Import Entire Framework',
					extraBtnIconAfter: 'fas fa-circle-arrow-right',
					acceptText: 'Import Branch or Asset',
					acceptIconAfter: 'fas fa-circle-arrow-right',
					dialogMaxWidth: 720,
				})
				if (branch_identifier == 'extra') {
					// user said to import all, so use framework_identifier as branch identifier
					branch_identifier = framework_identifier
					// clear lst value for collection_editor_case_branch_identifier
					this.collection_editor_case_branch_identifier = ''
				} else {
					if (branch_identifier == false) {
						return
					}
					branch_identifier = $.trim(branch_identifier)
					if (!U.is_uuid(branch_identifier)) {
						await this.$alert('The branch identifier must be a valid GUID.')
						return
					}
					if (!json.CFItems.find(x=>x.identifier == branch_identifier)) {
						await this.$alert('The specified branch identifier does not correspond to an item in the uploaded framework.')
						return
					}
					this.collection_editor_case_branch_identifier = branch_identifier
				}

				// construct framework_record for the uploaded json
				fr = U.create_framework_record(framework_identifier, json)
				U.framework_record_load_json(fr, json)
				U.framework_record_build_tree(fr)

			/////////////////////////////////
			// else user is specifying a framework from a case server
			} else {
				api_url = $.trim(api_url)

				// store provided url in lst
				this.collection_editor_case_import_url = api_url

				// look for a satchel url form, e.g. `https://case.georgiastandards.org/26f3c45c-c053-48a0-af55-c60b2a89033e/c08eef85-7347-4062-be7c-0cedd92121ca/2` for GAVS US History
				let smatch = api_url.match(
					/^(?:https|http):\/\/([^\/]+)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/
				)
				if (smatch) {
					let domain = smatch[1]
					framework_identifier = smatch[2]
					branch_identifier = smatch[3]

					// construct api from domain/framework_identifier
					api_url = `https://${domain}/ims/case/v1p1/CFPackages/${framework_identifier}`

				} else {
					// else look for a valid api
					if (api_url.search(/ims\/case\/v\w+\/CFPackages\/((\w|-)+)/) == -1) {
						await this.$alert('The url you entered is not valid. Note that valid API urls must include the following string: <nobr>“ims/case/v1p1/CFPackages”</nobr>')
						return
					}
					// extract framework_identifier from the api_url
					framework_identifier = RegExp.$1

					// then get branch identifier
					branch_identifier = await U.prompt_promise({
						title: dialog_title,
						text: 'Enter the identifier (GUID) for the “branch” of the CASE resource framework you’d like to import. Or click IMPORT ALL” to import all items from the framework.',

						// TEMP
						initialValue: 'c08eef85-7347-4062-be7c-0cedd92121ca',

						acceptText: 'Import Branch',
						acceptIconAfter: 'fas fa-circle-arrow-right',
						extraBtnText: 'Import All',
						dialogMaxWidth: 540,
					})
					if (branch_identifier == 'extra') {
						// user said to import all, so use framework_identifier as branch identifier
						branch_identifier = framework_identifier
					} else {
						if (branch_identifier == false) {
							return
						}
						branch_identifier = $.trim(branch_identifier)
						if (!U.is_uuid(branch_identifier)) {
							await this.$alert('The branch identifier must be a valid GUID.')
							return
						}
					}
				}

				// console.log('ok', api_url, framework_identifier, branch_identifier)
				U.loading_start()

				// look for an existing framework record for this framework; if not loaded, load it
				fr = U.retrieve_or_create_framework_record(framework_identifier)
				if (!fr.framework_json_loaded) {
					try {
						// if we're pulling from the satchel instance associated with this cureum instance, use get_lsdoc
						if (api_url.includes(this.$store.state.site_config.satchel_origin)) {
							await this.$store.dispatch('get_lsdoc', framework_identifier)

						} else {
							// else we can't use our get_lsdoc dispatch here, because we may be pulling from any case server, via the CASE API
							// so try to get the CASE data directly from the api_url
							let file_contents = await U.ajax_get_promise(api_url)
							// then parse it
							let json = JSON.parse(file_contents)
							U.framework_record_load_json(fr, json)
							U.framework_record_build_tree(fr)
						}

					} catch(e) {
						U.loading_stop()
						console.error(e)
						this.$alert('Error: couldn’t retrieve the specified CASE resource framework.')
						return
					}
				}
				U.loading_stop()
			}
			// end of code for processing a specified framework from a CASE server
			/////////////////////////////////

			console.log({ framework_identifier, branch_identifier, fr })
			return { framework_identifier, branch_identifier, fr }
		},

		async collection_import_from_case() {
			// normally we don't load the full lesson content for all lessons until we need it; do this to load all lesson content now so we can generate the full content of each one
			await this.$store.dispatch('get_learning_progression', {
				course_code: this.learning_progression.course_code,
				retrieve_full_lessons: true,
				force_reload: true,
			})

			// make sure folder_handling_option is reset
			this.folder_handling_option = null

			let collection = new Learning_Progression(this.learning_progression)

			let dialog_title = 'Import Units and Content from CASE Framework (BETA)'

			let { 
				framework_identifier, 
				branch_identifier, 
				fr 
			} = await this.import_from_case_start(dialog_title) || {}
			if (empty(framework_identifier)) {
				return
			}

			// NOTE: currently we are storing the crosswalk associations we want in the resource framework itself; in the future we might not want to count on this, so we might need to retrieve crosswalk frameworks here...

			let top_node, msg, item_type
			if (branch_identifier == framework_identifier) {
				top_node = fr.tree
				item_type = 'CFDocument'
				msg = `You have chosen to import all content from framework “<b>${top_node.cfitem.title}</b>”.`
			} else {
				top_node = fr.tree_nodes_hash[branch_identifier]
			}
			if (empty(top_node)) {
				this.$alert('Error: couldn’t find the specified branch in the CASE resource framework.')
				return
			} else {
				if (empty(item_type)) {
					item_type = U.item_type(top_node.cfitem) || 'CFItem'
					msg = `You have chosen to import content from ${item_type} “<b>${top_node.cfitem.humanCodingScheme||''} ${top_node.cfitem.fullStatement}</b>”.`
				}
			}

			// show the user what they're loading; make them confirm they want to load
			if (!empty(collection.case_definition_identifier)) {
				if (collection.case_definition_identifier != branch_identifier) {
					msg += `<div class="mt-2 red--text text--darken-2">Note that this item’s identifier (${branch_identifier}) does <b>not</b> match the Collection’s previously-saved case_definition_identifier (${collection.case_definition_identifier}).</div>`
				} else {
					msg += `<div class="mt-2 green--text text--darken-2"><i class="fas fa-circle-check" style="font-size:24px"></i> This item’s identifier <b>does</b> match the Collection’s previously-saved case_definition_identifier (${branch_identifier}).</div>`
				}
			} else {
				if (item_type.toLowerCase() != 'course') {
					msg += `<div class="mt-2 red--text text--darken-2">Note that item is <b>not</b> classified as a Course in the case framework.</div>`
				}
			}
			let val = await U.confirm_promise({
				title: 'Please Confirm',
				text: msg,
				acceptText: 'Proceed',
				acceptIconAfter: 'fas fa-arrow-right',
				dialogMaxWidth: 720,
			})
			if (!val) return
			// user confirmed
			
			// currently, all top-level items must be units to import to a collection
			for (let child of top_node.children) {
				if (!U.item_type_matches(child.cfitem, 'unit')) {
					this.$alert('To import content from CASE into a Collection, all top-level items in the specified CASE branch must be units; this is not the case for the branch you specified. You can edit individual Units of the Collection to import content directly into that Unit.')
					return
				}
			}

			let updated = false

			// if the collection is a course and top_node is a course, process settings for the course itself, unless 'no_cureum_update' is true
			if (collection.collection_type == 'course' && U.item_type_matches(top_node.cfitem, 'course')) {
				if (top_node.cfitem.extensions?.no_cureum_update !== true) {
					let changed = false

					// update case_definition_identifier if needed
					if (collection.case_definition_identifier != top_node.cfitem.identifier) {
						collection.case_definition_identifier = top_node.cfitem.identifier
						console.warn('case_definition_identifier set to ' + top_node.cfitem.identifier)
						changed = true
					}

					// update the collection's course_plan if we receive extensions
					if (!empty(top_node.cfitem.extensions)) {
						let old_course_plan = object_copy(collection.course_plan)
						if (collection.course_plan.update_from_case(top_node.cfitem.extensions)) {
							console.warn('course_plan changed (from, to)')
							console.log(old_course_plan)
							console.log(object_copy(collection.course_plan))
							changed = true
						}
					}

					// something was changed, save collection here, without sending any units
					if (changed) {
						let confirmed = await U.confirm_promise({
							text: 'Course metadata has been <b>updated</b> (see console for details). Do you want to save these updates?',
							acceptText: 'Save',
							acceptIcon: 'fas fa-save',
							extraBtnText: 'Skip Metadata Updates',
							extraBtnIconAfter: 'fas fa-arrow-right',
							cancelText: 'Abort Import',
							cancelIcon: 'fas fa-xmark',
							dialogMaxWidth: 600,
						})

						// if user clicks "Abort Import", bail out
						if (!confirmed) {
							return

						// if user clicks "Skip" ('extra' btn), continue to units; otherwise save
						} else if (confirmed !== 'extra') {
							// send this so that we don't save any units
							collection.unit_index_to_save = -2
							let slp_result = await this.$store.dispatch('save_learning_progression', collection)
							if (empty(slp_result)) {
								await U.alert_promise('An error occurred when attempting to save.')
								window.location.reload()
							}
							console.warn('slp_result', slp_result)
						}

						updated = true

					// no updates; ask user if they want to proceed
					} else {
						let response = await U.confirm_promise({
							text: 'Course metadata not updated. Proceed to process units?',
							acceptText: 'Proceed',
							acceptIcon: 'fas fa-arrow-right',
							cancelText: 'Abort Import',
							cancelIcon: 'fas fa-xmark',
							dialogMaxWidth: 500,
						})
						if (!response) {
							return
						}
					}
				}
			}

			// import/offer to save each unit
			for (let node of top_node.children) {
				// if a unit with this case_definition_identifier already exists, update it; otherwise make a new LP_Unit
				let unit = collection.units.find(x=>x.case_definition_identifier == node.cfitem.identifier)
				if (empty(unit)) {
					unit = new LP_Unit()
				}

				// call unit_import_from_case to process the unit
				let result = await this.unit_import_from_case(true, collection, unit, fr, node)

				// if user canceled, break from the rest of the units; if an update occurred for the unit, update updated
				if (result == 'abort') {
					// ?? close the collection editor; otherwise the checkin/checkout stuff gets messed up
					this.cancel_editor()
					break

				} else if (result == 'unit_updated') {
					updated = true
				}
				// if result is 'unit_skipped', we'll just coninue to the next unit
			}

			if (updated) {
				await U.alert_promise('Click OK to reload the window and view unit updates.')
			}
			// always reload, just in case something might have changed...
			window.location.reload()
		},

		async unit_import_from_case(from_collection, collection, unit, fr, top_node) {
			let unit_status, import_top_level_item
			// if we haven't been called from the collection_import fn, we've been called from the unit editor
			if (!from_collection) {
				// normally we don't load the full lesson content for all lessons until we need it; do this to load all lesson content now so we can generate the full content of each one
				await this.$store.dispatch('get_learning_progression', {
					course_code: this.collection.course_code,
					retrieve_full_lessons: true,
					force_reload: true,
				})

				// make sure folder_handling_option is reset
				this.folder_handling_option = null

				// prompt the user to specify, then retrieve, the case data
				let dialog_title = 'Import Content from CASE Framework (BETA)'
				let o = await this.import_from_case_start(dialog_title) || {}
				if (empty(o.framework_identifier)) {
					return
				}

				fr = o.fr

				// add folders and assets for the specified branch to the unit
				// not sure if there's a really context where we would load everything from a framework into a single unit, but we'll allow it for now
				let msg, item_type
				if (o.branch_identifier == o.framework_identifier) {
					top_node = fr.tree
					item_type = 'CFDocument'
					msg = `You have chosen to import all content from framework “<b>${top_node.cfitem.title}</b>”.`
				} else {
					top_node = fr.tree_nodes_hash[o.branch_identifier]
				}

				if (empty(top_node)) {
					this.$alert('Error: couldn’t find the specified CFItem in the CASE resource framework.')
					return
				} else {
					if (empty(item_type)) {
						item_type = U.item_type(top_node.cfitem) || 'CFItem'
						msg = `You have chosen to import content from ${item_type} “<b>${top_node.cfitem.humanCodingScheme ? top_node.cfitem.humanCodingScheme + ' ' : ''}${top_node.cfitem.fullStatement}</b>”.`
					}
				}

				///////////////////////////////////////////////
				// show the user what they're loading; make them confirm they want to load
				// if the specified top_node has item type unit...
				if (U.item_type_matches(top_node.cfitem, 'unit')) {
					// we don't import the top-level unit as an item itself; we only import metadata from the unit item
					import_top_level_item = false

					// if the unit has case_definition_identifier set...
					if (!empty(this.unit.case_definition_identifier)) {
						// then warn the user if this new branch doesn't match the unit's case_definition_identifier
						if (this.unit.case_definition_identifier != o.branch_identifier) {
							msg += `<div class="mt-2 red--text text--darken-2">Note that the specified CFItem’s identifier (${o.branch_identifier}) does <b>not</b> match the Unit’s previously-saved case_definition_identifier (${this.unit.case_definition_identifier}).</div>`

						// else assure the user that we have a match
						} else {
							msg += `<div class="mt-2 green--text text--darken-2"><i class="fas fa-circle-check" style="font-size:24px"></i> The specified CFItem’s identifier matches the Unit’s previously-saved case_definition_identifier (${o.branch_identifier}).</div>`
						}

					// the itemType a Unit and this unit doesn't have case_definition_identifier set, so inform them what will happen
					} else {
						msg += `<div class="mt-2">Since the specified CFItem has Item Type “Unit”, metadata from the CFItem (e.g. title, duration, etc.) will be transferred to the Unit, along with all assets nested under the CFItem.</div>`
					}

					msg += `<div class="mt-2">Any existing folders and/or assets will be updated, if necessary, with metadata from the CASE framework.</div>`

					let val = await U.confirm_promise({
						title: 'Please Confirm',
						text: msg,
						acceptText: 'Proceed',
						acceptIconAfter: 'fas fa-arrow-right',
						dialogMaxWidth: 720,
					})
					if (!val) return

				// else the specified top_node isn't a unit.
				// if top_node is a framework, tell them this...
				} else if (!empty(top_node.cfitem.title)) {
					// we never import the framework itself (I suppose we could, but that would complicate things even further)
					import_top_level_item = false

					msg += `<div class="mt-2">All assets (including “folders”) from the framework will be imported into the Unit.</div>`
					msg += `<div class="mt-2">Any existing folders and/or assets in the framework will be updated, if necessary, with metadata from the CASE framework.</div>`

					let val = await U.confirm_promise({
						title: 'Please Confirm',
						text: msg,
						acceptText: 'Proceed',
						acceptIconAfter: 'fas fa-arrow-right',
						dialogMaxWidth: 720,
					})
					if (!val) return

				// if top_node is a lesson or has no children, we're just importing the one item...
				} else if (U.item_type_matches(top_node.cfitem, 'Lesson') || top_node.children.length == 0) {
					// if it's one item, we're obviously going to import it
					import_top_level_item = true

					msg += `<div class="mt-2">This CFItem represents a single asset that will be imported into the Unit. If it already exists, it will be updated, if necessary, with metadata from the CASE item.</div>`

					let val = await U.confirm_promise({
						title: 'Please Confirm',
						text: msg,
						acceptText: 'Proceed',
						acceptIconAfter: 'fas fa-arrow-right',
						dialogMaxWidth: 720,
					})
					if (!val) return

				// else the specified top_node is a non-unit folder...
				} else {
					// ask if the user wants to import the top-level folder itself
					msg += `<div class="mt-2">Do you wish to import the <b>top-level “folder” itself</b> into the Unit, or import <b>children of the top-level folder only</b>?</div>`
					msg += `<div class="mt-2">Whichever option you choose, all assets (including “sub-folders”) from the top-level folder will be imported into the Unit, and any existing folders and/or assets in the framework will be updated, if necessary, with metadata from the CASE framework.</div>`

					// so ask the user if they want to import the folder and its descendents, or just the descendents
					let val = await U.confirm_promise({
						title: 'Please Confirm',
						text: msg,
						extraBtnText: 'Import Folder + Children',
						extraBtnIconAfter: 'fas fa-arrow-right',
						acceptText: 'Import Children Only',
						acceptIconAfter: 'fas fa-arrow-right',
						dialogMaxWidth: 720,
					})
					if (!val) return
					if (val == 'extra') {
						import_top_level_item = true
					} else {
						import_top_level_item = false
					}
				}

				// if we get to here, the user has confirmed they want to proceed

				// in this context, we've been called from CollectionUnitEdit
				// so start with a copy of original_unit from the CollectionUnitEdit interface
				unit = new LP_Unit(this.original_unit)
				unit_status = 'unchanged'

				// and use this.collection as collection. if we don't save, this won't be altered; if we do save, we'll immediately reload the window
				collection = this.collection

			// if we have been called from collection_import, we've been called from the collection editor, and this is one unit of the collection...
			} else {
				// unit will either be a new unit or an existing one
				if (unit.lp_unit_id == 0) {
					unit_status = 'new'
				} else {
					unit_status = 'unchanged'
				}
				// fr and top_node will come in from the caller

				// this will definitionally be a Unit CFItem, so don't import the CFItem itself
				import_top_level_item = false
			}

			// if we haven't established folder_handling_option, do so now
			if (this.folder_handling_option == null) {
				let msg = ''
				msg += `If the CASE data ${from_collection ? 'for any unit' : ''} includes folders, how would you like to handle folders and folder assignments?`
				msg += `<ol class="mt-3">`
				msg += `<li><b>Mimic the folder structure from the CASE framework:</b> Add the folders (if they don’t already exist) to the unit, and arrange assets inside those folders</li>`
				msg += `<li class="mt-1"><b>Import or update assets only:</b> leave existing assets arranged in the folder structure already reflected in the unit, and import new assets as top-level items in the unit.</li>`
				msg += `</ol>`

				let val = await U.confirm_promise({
					title: 'Folder Handling',
					text: msg,
					extraBtnText: '1. Mimic CASE Folders',
					extraBtnIconAfter: 'fas fa-arrow-right',
					acceptText: '2. Import Assets Only',
					acceptIconAfter: 'fas fa-arrow-right',
					dialogMaxWidth: 720,
				})
				if (!val) return
				if (val == 'extra') {
					this.folder_handling_option = 'import_folders'
				} else {
					this.folder_handling_option = 'assets_only'
				}
			}

			///////////////////////////////////////////////
			// at this point we've done the initial warning to the user about the selected CFItem
			// now process the item and descendents to find out what exactly will be imported

			// if top_node is the unit itself, process settings for the unit itself, unless 'no_cureum_update' is true
			if (U.item_type_matches(top_node.cfitem, 'unit')) {
				if (top_node.cfitem.extensions?.no_cureum_update !== true) {
					let unit_item = top_node.cfitem

					// map case data to cureum data
					let ud = {
						case_definition_identifier: unit_item.identifier,
						// note mappings here
						title: unit_item.fullStatement,
						display_number: unit_item.humanCodingScheme ?? '',
						duration: unit_item.extensions?.duration ?? '',
						text: unit_item.extensions?.description ?? '',
					}

					// corrections/cleanup...
					// “3 weeks of instruction” => “3”
					ud.duration = ud.duration.replace(/^(\S+) +(week(s)?)[\s\S]*/i, '$1')
					// if (!empty(ud.text)) ud.text = U.marked_latex(ud.text)
					// TODO: CPS-specific...
					ud.display_number = ud.display_number.replace(/.*\.U(.+)/, 'Unit $1')
					
					// update unit settings if necessary
					for (let key in ud) {
						if (unit[key] != ud[key]) {
							console.warn(`${key} changed (from, to)`)
							console.log(unit[key])
							console.log(ud[key])
							unit[key] = ud[key]
							unit_status = 'changed'
						}
					}

					// update the unit's unit_plan if we receive extensions
					if (!empty(top_node.cfitem.extensions)) {
						let old_unit_plan = object_copy(unit.unit_plan)
						if (unit.unit_plan.update_from_case(top_node.cfitem.extensions)) {
							console.warn('unit_plan changed (from, to)')
							console.log(old_unit_plan)
							console.log(object_copy(unit.unit_plan))
							unit_status = 'changed'
						}
					}
			
					// standards for the unit
					let assocs = fr.associations_hash[top_node.cfitem.identifier]
					if (assocs) {
						let previous_unit_standards = JSON.stringify(unit.standards.map(item => item.identifier))
						for (let assoc of assocs) {
							let assoc_item = await U.get_associated_item(assoc, top_node.cfitem.identifier, false)
							if (assoc_item) {
								// note that the way this works, we will add standards from CASE to whatever standards currently exist for the asset in Cureum, but we won't delete standards that already exist in Cureum
								unit.add_standard(assoc_item, true)
							}
						}
						let new_unit_standards = JSON.stringify(unit.standards.map(item => item.identifier))
						if (new_unit_standards != previous_unit_standards) {
							console.log('standards changed')
							unit_status = 'changed'
						}
					}
				}
			}

			// prepare for new items to be added to the unit
			let ca_mapping_data = {
				folders: [],
				assets: [],
				asset_counts: {
					new: 0,
					changed: 0,
					unchanged: 0
				}
			}

			U.loading_start()
			// if we're not adding the top-level item
			if (!import_top_level_item) {
				// just add its children
				for (let child of top_node.children) {
					await this.process_resources_from_case_branch(unit, unit, fr, ca_mapping_data, child, 'top')
				}

			// else add the selected asset/folder and anything underneath it
			} else {
				await this.process_resources_from_case_branch(unit, unit, fr, ca_mapping_data, top_node, 'top')
			}
			U.loading_stop()

			console.log(object_copy(unit), ca_mapping_data)

			// report on the unit
			let text = `<b>${unit.display_number} ${unit.title}</b><ul class="mt-2">`

			text += '<li>'
			if (unit_status == 'unchanged') text += 'This unit’s metadata (title, duration, etc.) is <b>unchanged</b>.'
			else if (unit_status == 'changed') text += 'This unit’s metadata (title, duration, etc.) will be <b>updated</b>.'
			else text += 'This unit will be <b>created</b>.'
			text += '</li>'

			if (ca_mapping_data.folders.length > 0 || ca_mapping_data.assets.length > 0) {
				text += `<li>${ca_mapping_data.assets.length} asset(s) found:<ul>`
					text += `<li>${ca_mapping_data.asset_counts.new} will be <b>created</b></li>`
					text += `<li>${ca_mapping_data.asset_counts.changed} will be <b>updated</b></li>`
					text += `<li>${ca_mapping_data.asset_counts.unchanged} is/are <b>unchanged</b></li>`
				text += `</ul></li>`

				// if we think there are no new or changed items...
				if (ca_mapping_data.asset_counts.new == 0 && ca_mapping_data.asset_counts.changed == 0) {
					// if we're saving folder assignments add this note
					if (this.folder_handling_option == 'import_folders') {
						text += '<div class="mt-1">(Note that the unit folder structure may been updated if you choose to SAVE.)</div>'
						unit_status = 'changed'
					}
				} else {
					unit_status = 'changed'
				}

				// send summary of what will be changed to console
				let summary = {new: [], changed: [], unchanged: []}
				for (let asset of ca_mapping_data.assets) {
					summary[asset.IMPORT_STATUS].push(`${asset.IMPORT_STATUS.toUpperCase()}: ${asset.human_code} ${asset.lesson_title || asset.description}`)
				}
				console.warn('------------------------')
				console.warn('Summary of unit assets')
				for (let s of summary.new) console.log(s)
				for (let s of summary.changed) console.log(s)
				for (let s of summary.unchanged) console.log(s)
				console.warn('------------------------')
			}

			text += '</ul>'

			// if the user is processing all units...
			if (from_collection) {
				let confirmed = await U.confirm_promise({
					text,
					acceptText: 'Save This Unit',
					acceptIcon: 'fas fa-save',
					extraBtnText: 'Skip This Unit',
					extraBtnIconAfter: 'fas fa-arrow-right',
					cancelText: 'Abort Import',
					cancelIcon: 'fas fa-xmark',
					dialogMaxWidth: 600,
				})

				// if user clicks "Abort Import", bail out
				if (!confirmed) {
					return 'abort'

				// if user clicks "Skip This Unit", continue
				} else if (confirmed === 'extra') {
					return 'unit_skipped'
				}
				// else user clicked save, so proceed to save below

			// else we're just processing one unit...
			} else {
				let confirmed = await U.confirm_promise({
					title: 'Confirm Save',
					text,
					acceptText: 'Save',
					acceptIcon: 'fas fa-save',
				})

				// if user says cancel, just return -- nothing has been changed
				if (!confirmed) {
					return
				}
			}

			// if we get to here, finish processing, then save, this unit
			let ca_mapping_payload_assets = this.prepare_unit_assets_for_save(collection, unit, ca_mapping_data)

			// add the unit to, or replace the unit in, collection.units
			let unit_index_to_save = collection.units.findIndex(x=>x.lp_unit_id == unit.lp_unit_id)
			if (unit_index_to_save > -1) {
				collection.units.splice(unit_index_to_save, 1, unit)
			} else {
				unit_index_to_save = collection.units.length
				collection.units.push(unit)
			}

			// note that we don't use the save_edits/save_unit_edits fns in CollectionEdit/CollectionUnitEdit, because we don't want/need to execute all the logic there
			// so we just do the save_learning_progression dispatch on collection

			// we only want to send this unit's data to the server; setting unit_index_to_save triggers the store to do this
			collection.unit_index_to_save = unit_index_to_save

			console.warn('save collection unit ' + unit_index_to_save, object_copy(collection))
			// return

			let slp_result = await this.$store.dispatch('save_learning_progression', collection)
			if (empty(slp_result)) {
				await U.alert_promise('An error occurred when attempting to save.')
				window.location.reload()
			}
			console.warn('slp_result', slp_result)

			// after save_learning_progression is done, update unit_ids in ca_mapping_payload, in case we just created it
			for (let o of ca_mapping_payload_assets) {
				o.unit_id = unit.lp_unit_id
			}

			// then save the ca_mappings
			// stringify and encode_blocked_keywords on the assets param we send in to add_collection_asset_mapping, because a) we might have a lot of them, and b) the asset metadata may include stuff that triggers firewalls
			let ca_mapping_payload = {
				user_id: this.user_info.user_id,
				assets: U.encode_blocked_keywords(JSON.stringify(ca_mapping_payload_assets)),
				encode_blocked_keywords: ['assets'],
			}
			U.loading_start()
			await unit.add_items(ca_mapping_payload)
			console.warn('unit.add_items done')
			U.loading_stop()

			// if we're processing a single unit, reload now
			if (!from_collection) {
				await U.alert_promise('Click OK to reload the window and view unit updates.')
				window.location.reload()

			// else return unit_status
			} else {
				return unit_status
			}
		},

		async create_folder(unit, ca_mapping_data, fid, folder_color, folder_title) {
			// create a folder with the fid, folder_color, and folder_title, 
			// unless it already exists in the existing unit or in the ca_mapping_data we're already adding
			if (!unit.resource_tree.folders.find(x=>x.folder_id == fid)) {
				if (!ca_mapping_data.folders.find(x=>x.folder_id == fid)) {
					ca_mapping_data.folders.push({
						parent_folder_id: 'top',
						folder_id: fid,
						color: folder_color,
						title: folder_title,
					})
				}
			}
		},

		async add_resource_to_lesson_plan_component(lesson_asset, lc_uuid, resource) {
			let changed = false
			// add to the component's lc_asset_ids
			let lc = lesson_asset.lesson_plan.find(x=>x.lc_uuid == lc_uuid)
			if (empty(lc)) {
				console.warn(`adding ${lc_uuid} component`)
				lc = new Lesson_Component_B({lc_uuid:lc_uuid})
				lesson_asset.lesson_plan.push(lc)
			}
			if (!lc.lc_asset_ids.includes(resource.resource_id)) {
				lc.lc_asset_ids.push(resource.resource_id)
				changed = true
			}

			// also add to the lesson resources
			if (!lesson_asset.resources.find(x=>x.resource_id == resource.resource_id)) {
				lesson_asset.resources.push(resource)
				changed = true
			}

			return changed
		},

		// recursively add folders and assets from a CASE resource framework
		// however, here we just *prepare* to add them; then we ask the user to confirm before actually adding things
		// `unit` is always a unit itself; `resource_holder` could be the unit, or it could be a lesson
		async process_resources_from_case_branch(unit, resource_holder, fr, ca_mapping_data, node, parent_folder_id) {
			let cfitem = node.cfitem
			let cfitem_type = U.item_type(cfitem, true)

			// if (node has children, or it has no item type, or its item type is 'folder'), it's a folder, UNLESS it is a lesson (which can have child resources)
			if ((node.children.length > 0 || cfitem_type == 'folder' || empty(cfitem_type)) && cfitem_type != 'lesson') {
				if (empty(cfitem_type) && node.children.length == 0) {
					// console.warn('EMPTY FOLDER: ' + cfitem.fullStatement)
				}

				// note that folders will never be added to lessons, so 'unit' and 'resource_holder' will always be the same thing here
				// console.log('add folder: ' + node.cfitem.fullStatement)
				let new_parent_folder_id
				// if we didn't receive a parent_folder_id, this is the "root branch", which we *don't* want to add to the unit (at least at this time)
				if (empty(parent_folder_id)) {
					// add children to the 'top' of the unit
					new_parent_folder_id = 'top'

				} else {
					// else record the new parent folder_id
					new_parent_folder_id = node.cfitem.identifier

					// and add the folder to ca_mapping_data, if folder_handling_option says to do so
					if (this.folder_handling_option == 'import_folders') {
						ca_mapping_data.folders.push({
							parent_folder_id: parent_folder_id,
							folder_id: new_parent_folder_id,
							title: node.cfitem.fullStatement,
						})
					}
				}

				// then recursively add the children (each of which could be a sub-folder or an asset)
				for (let child of node.children) {
					await this.process_resources_from_case_branch(unit, resource_holder, fr, ca_mapping_data, child, new_parent_folder_id)
				}

			// else it should be an asset
			} else {
				// console.log(object_copy(cfitem))
				// console.log('  add asset: ' + cfitem.fullStatement)

				// TODO: try to load full asset here; it may or may not already be loaded in full

				let asset, previous_standards

				// note that on 2/21/2026, PW moved the logic for (mapping case on to cureum) to Lesson.case_to_lesson_properties for lessons, but didn't yet move this logic for resources

				// lesson
				if (cfitem_type == 'lesson') {
					// note that lessons will never be added to other lessons, so 'unit' and 'resource_holder' will always be the same thing here (at least for now...)
					console.log('** Processing lesson ' + cfitem.fullStatement)
					// note: we assume everything we import from CASE is variant 'B' lesson plans (LPC)

					// if the lesson corresponding to this identifier already exists, make a copy of it
					let e_lesson = unit.lessons.find(x=>x.case_definition_identifier == cfitem.identifier)
					if (e_lesson) {
						previous_standards = JSON.stringify(e_lesson.standards.map(item => item.identifier))
						asset = new Lesson(object_copy(e_lesson))

						// apply case data to the lesson, telling the algorithm to use case as the source of truth
						let changed = asset.case_to_lesson_properties(cfitem, 'case')

						// set asset.IMPORT_STATUS appropriately
						asset.IMPORT_STATUS = changed ? 'changed' : 'unchanged'

					// otherwise make a new lesson
					} else {
						asset = new Lesson({lp_variant: 'B'})

						// apply case data to the lesson, telling the algorithm to use case as the source of truth
						asset.case_to_lesson_properties(cfitem, 'case')
						asset.IMPORT_STATUS = 'new'
					}

					// we may apply other changes below...
					let another_change_made = false
					// HACK TODO: for WL, explicitly set lp_component_scheme
					if (asset.lp_component_scheme != 'world_languages') {
						console.warn(`updating lp_component_scheme`)
						asset.lp_component_scheme = 'world_languages'
						another_change_made = true
					}

					// lessons can have child resources (e.g. Sparkl activities for CPS WL lessons); process and add these
					// for now we're just doing what we need for CPS WL; we can extend this later if needed
					if (node.children.length > 0) {
						for (let child of node.children) {
							// process a child assessment...
							if (child.cfitem.CFItemType == 'Assessment') {
								// process_resources_from_case_branch will put these in a special folder
								let resource = await this.process_resources_from_case_branch(unit, asset, fr, ca_mapping_data, child, null)

								// add to the 'assessment' component's lc_asset_ids
								if (await this.add_resource_to_lesson_plan_component(asset, 'assessment', resource)) {
									console.warn(`adding assessment “${resource.human_code}”`)
									another_change_made = true
								}

							// process a child Learning Resource...
							} else if (child.cfitem.CFItemType == 'Learning Resource') {
								// process_resources_from_case_branch will put these in a special folder
								let resource = await this.process_resources_from_case_branch(unit, asset, fr, ca_mapping_data, child, null)

								// if this is a "mini-lesson" activity, add to differentiation.lc_asset_ids
								if (resource.human_code.includes('AM')) {
									if (await this.add_resource_to_lesson_plan_component(asset, 'differentiation', resource)) {
										console.warn(`adding resource “${resource.human_code}”`)
										another_change_made = true
									}

								// else add to lesson_flow.lc_asset_ids
								} else {
									if (await this.add_resource_to_lesson_plan_component(asset, 'lesson_flow', resource)) {
										console.warn(`adding resource “${resource.human_code}”`)
										another_change_made = true
									}
								}
							}
						}
					}

					// if we made an additional change above, set IMPORT_STATUS to 'changed'
					if (another_change_made && asset.IMPORT_STATUS != 'new') asset.IMPORT_STATUS = 'changed'

					// HACK: if no_cureum_update is true, skip the asset -- but do it after we deal with the resources
					// TODO: do something more sophisticated than this??
					if (cfitem.extensions?.no_cureum_update == true) {
						asset.IMPORT_STATUS = 'unchanged'
						console.log('  SKIP asset (no_cureum_update): ' + cfitem.fullStatement)
						return
					}
					
				// or resource
				} else {
					console.log('** Processing resource ' + cfitem.fullStatement)
					// map case data to cureum data
					// NOTE: no case_definition_identifier for resources, because we use the CASE guid as the resource_id
					let ad = {
						description: cfitem.fullStatement,
						human_code: cfitem.humanCodingScheme ?? '',
						url: cfitem.extensions?.resourceURL || '',
					}

					if (!empty(cfitem.extensions?.description)) {
						ad.long_description = U.marked_latex(cfitem.extensions.description)
					}
					
					// TODO: save publisher and license in CASE, then import the same here

					////////////
					// Determine the cureum resource type via the following logic:
					// by default we'll treat the resource as type 'website' (this should usually correspond to deliveryMechanism `openURL`)
					let type = 'website'
					// for google drive, default is 'document'
					if (ad.url.search(/(drive|docs)\.google\.com/) > -1) {
						type = 'document'

					// sparkl activities
					} else if (cfitem.extensions?.deliverySystem == 'sparkl') {
						type = 'sparkl'

						// url should be just the activity_id
						// TODO: this regexp may need to be generalized...
						ad.url = cfitem.extensions.resourceURL.replace(/.*\/(\d+)/, '$1')

						// add summary to long_description
						ad.long_description = cfitem.extensions.summary || ''

						// TODO: for sparkls, we have to figure out how/when to migrate the activity to this cureum's sparkl instance

					// lti resources
					} else if (cfitem.extensions?.deliveryMechanism == 'lti') {
						type = 'lti'
						// get lti params
						if (!empty(cfitem.extensions?.ltiParams)) {
							try { 
								ad.lti_params = JSON.parse(cfitem.extensions?.ltiParams) 
							} catch(e) {
								console.warn('lti_param parse failed', e)
							}
						} else {
							ad.lti_params = {}
						}
						if (cfitem.extensions?.ltiLaunchURL) {
							ad.lti_params.launch_url = cfitem.extensions.ltiLaunchURL
						}

					// assessments (tuned for CPS)
					} else if (cfitem_type == 'assessment') {
						type = 'assessment'
						if (cfitem.extensions?.assessmentSourcedId) {
							ad.url = 'cps-checkpoint-sid-' + cfitem.extensions.assessmentSourcedId
						}
						if (cfitem.extensions?.assessmentInfoURL) {
							ad.supp_links = [
								{
									description: '',
									type: 'blueprint',
									url: cfitem.extensions.assessmentInfoURL
								}
							]
						}
					}

					ad.type = type
					////////////

					// teacherFacing
					if ((cfitem.extensions?.teacherOnly+'') == 'true') {
						ad.teacher_facing = true
					}

					let extensions = {}
					if (cfitem.extensions?.provider) {
						extensions.content_source = cfitem.extensions?.provider
					}
					if (cfitem.extensions?.learningResourceType) {
						extensions.content_type = cfitem.extensions?.learningResourceType
					}
					// TODO: map other extensions onto defined cureum Resource fields?

					// if we're updating (as opposed to inserting new resources), start with the existing resource, so that we preserve things that aren't in the case data (e.g. agency_sanctioned)
					let e_resource = resource_holder.resources.find(x=>x.resource_id == cfitem.identifier)
					if (empty(e_resource) && resource_holder != unit) {
						e_resource = unit.resources.find(x=>x.resource_id == cfitem.identifier)
					}
					if (e_resource) {
						previous_standards = JSON.stringify(e_resource.standards.map(item => item.identifier))
						asset = new Resource(object_copy(e_resource))
						asset.IMPORT_STATUS = 'unchanged'	// we'll delete this field before saving
						for (let key in ad) {
							if (JSON.stringify(asset[key]) != JSON.stringify(ad[key])) {
								console.warn(`update resource “${key}”`, JSON.stringify(asset[key]), JSON.stringify(ad[key]))
								asset.IMPORT_STATUS = 'changed'
								asset[key] = ad[key]
							}
						}
						if (U.object_has_keys(extensions)) {
							if (empty(asset.extensions)) {
								asset.IMPORT_STATUS = 'changed'
								asset.extensions = extensions
							} else {
								for (let key in extensions) {
									if (asset.extensions[key] != extensions[key]) {
										asset.IMPORT_STATUS = 'changed'
										asset.extensions[key] = extensions[key]
									}
								}
							}
						}

					} else {
						ad.resource_id = cfitem.identifier
						if (U.object_has_keys(extensions)) ad.extensions = extensions
						asset = new Resource(ad)
						asset.IMPORT_STATUS = 'new'
					}

					// store extensions
					asset.case_resource_extensions = cfitem.extensions

					// for skyline...
					if (this.$store.state.site_config.app_name == 'Skyline') {
						// add assessments to "Unit Assessments" folder in the unit, creating if the folder doesn't exist
						if (asset.type == 'assessment') {
							parent_folder_id = 'case_import_unit_assessments'
							await this.create_folder(unit, ca_mapping_data, parent_folder_id, 'pink', 'Unit Assessments')

						// and add sparkls to "Unit Activities" folder in the unit, creating if the folder doesn't exist
						} else if (asset.type == 'sparkl') {
							// this is called 'case_import_lesson_activities' instead of 'case_import_unit_activities' for legacy reasons...
							parent_folder_id = 'case_import_lesson_activities'
							await this.create_folder(unit, ca_mapping_data, parent_folder_id, 'purple', 'Unit Activities')
						}
					}
				}

				// standards alignments (these work the same for resources and lessons)
				let assocs = fr.associations_hash[cfitem.identifier]
				if (assocs) {
					for (let assoc of assocs) {
						let assoc_item = await U.get_associated_item(assoc, cfitem.identifier, false)
						if (assoc_item) {
							// note that the way this works, we will add standards from CASE to whatever standards currently exist for the asset in Cureum, but we won't delete standards that already exist in Cureum
							asset.add_standard(assoc_item, true)

							// for now at least, always add all standards for lessons to the standards for the unit
							if (cfitem_type == 'lesson') {
								unit.add_standard(assoc_item, true)
							}
						}
					}
					let new_standards = JSON.stringify(asset.standards.map(item => item.identifier))
					if (asset.IMPORT_STATUS == 'unchanged' && previous_standards != new_standards) {
						console.warn(`updating standards`)
						asset.IMPORT_STATUS = 'changed'
					}
				}

				// if we received a parent_folder_id, set it for use later
				if (!empty(parent_folder_id)) {
					asset.parent_folder_id = parent_folder_id	// we'll take this out if/when we save
				}
				ca_mapping_data.assets.push(asset)
				ca_mapping_data.asset_counts[asset.IMPORT_STATUS] += 1

				// return the asset
				return asset
			}
		},

		prepare_unit_assets_for_save(collection, unit, ca_mapping_data) {
			let assets = []

			// add/update the folders and assets in the unit
			for (let folder of ca_mapping_data.folders) {
				let f = unit.resource_tree.folders.find(x=>x.folder_id == folder.folder_id)
				if (!empty(f)) {
					// console.log(' !folder exists')
					// note that we don't update parent_folder_id for folders, so if it's been moved, it'll stay where it is
					f.title = folder.title
				} else {
					unit.create_resource_folder(folder)
				}
			}

			for (let asset of ca_mapping_data.assets) {
				// extract then delete parent_folder_id
				let parent_folder_id = asset.parent_folder_id
				delete asset.parent_folder_id

				// ditto import status
				let import_status = asset.IMPORT_STATUS
				delete asset.IMPORT_STATUS

				let asset_type, asset_id, fa_resource_id
				if (!empty(asset.lesson_title)) {
					if (import_status != 'unchanged') {
						let ai = unit.lessons.findIndex(x=>x.case_definition_identifier == asset.case_definition_identifier)
						if (ai == -1) {
							unit.lessons.push(asset)
						} else {
							unit.lessons.splice(ai, 1, asset)
							// console.log('! Lesson exists')
						}
					}

					asset_type = 'lesson'
					asset_id = asset.case_definition_identifier

					// fa_resource_id should be the lesson_id if we have one, or the case_definition_identifier
					if (asset.lesson_id > 0) fa_resource_id = asset.lesson_id
					else fa_resource_id = asset.case_definition_identifier

				// else resource
				} else {
					if (import_status != 'unchanged') {
						let ai = unit.resources.findIndex(x=>x.resource_id == asset.resource_id)
						if (ai == -1) {
							unit.resources.push(asset)
						} else {
							unit.resources.splice(ai, 1, asset)
							// console.log('! Resource exists')
						}
					}

					asset_type = 'resource'
					asset_id = asset.resource_id
					fa_resource_id = asset.resource_id
				}

				// if we're importing folders...
				if (this.folder_handling_option == 'import_folders') {
					// remove any existing folder assignment if there (assignment could in theory be to either fa_resource_id or asset_id)
					unit.remove_item_folder_assignment(fa_resource_id)
					unit.remove_item_folder_assignment(asset_id)

					// we should always have a parent_folder_id, but in case we don't use 'top'
					if (empty(parent_folder_id)) parent_folder_id = 'top'

					// add the assignment we want
					unit.add_item_to_folder({
						type: asset_type,
						resource_id: fa_resource_id,
						parent_folder_id: parent_folder_id,
						seq: unit.get_max_folder_sequence(parent_folder_id) + 1
					})

					// if the asset has a parent_folder_id and the user has said they want to save folder assignments,...
						// // see if the asset already has a folder_assignment
						// let fai = unit.resource_tree.folder_assignments.findIndex(x=>x.resource_id == fa_resource_id || x.resource_id == asset_id)

						// // if we have an existing fa to a *different* folder (including 'top'), remove that fa
						// if (fai > -1) {
						// 	let fa = unit.resource_tree.folder_assignments[fai]
						// 	if (fa.parent_folder_id != parent_folder_id) {
						// 		unit.remove_item_folder_assignment(fa_resource_id)
						// 		fai = -1
						// 	}
						// }
						// // if the correct folder assignment already exists, leave it alone
						// // note that there may be an edge case...

						// if (fai == -1) {
						// 	unit.add_item_to_folder({
						// 		type: asset_type,
						// 		resource_id: fa_resource_id,
						// 		parent_folder_id: parent_folder_id,
						// 		seq: unit.get_max_folder_sequence(parent_folder_id) + 1
						// 	})
						// }
					
				// else we're not importing folders, so...
				} else {
					// see if the asset already has a folder_assignment
					let fai = unit.resource_tree.folder_assignments.findIndex(x=>x.resource_id == fa_resource_id || x.resource_id == asset_id)

					// if not, add the item to 'top'
					if (fai == -1) {
						unit.add_item_to_folder({
							type: asset_type,
							resource_id: fa_resource_id,
							parent_folder_id: 'top',
							seq: unit.get_max_folder_sequence('top') + 1
						})
					}
				}

				let o = {
					asset_type: asset_type,
					asset_id: asset_id,
					lp_id: collection.lp_id,
					unit_id: unit.lp_unit_id,
				}
				if (import_status != 'unchanged') {
					// by sending in the asset_data, we'll create or update the asset when unit.add_items calls add_collection_asset_mapping
					o.asset_data = asset.copy_for_save()
				}
				assets.push(o)
			}

			return assets
		},
	}
}
